import React, { useState, useEffect, useRef } from 'react'
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  PaymentRequestButtonElement
} from '@stripe/react-stripe-js'
import debounce from 'lodash.debounce'
import { ajaxPost, postForm } from '../../../ajax'

export default function StripeForm({league, type, token, cards, defaultCard, formData, onBack, onComplete}) {
  const stripe = useStripe()
  const elements = useElements()
  const [method, setMethod] = useState('card')
  const [paymentRequest, setPaymentRequest] = useState(null)
  const [complete, setComplete] = useState({})
  const [error, setError] = useState()
  const [processing, setProcessing] = useState(false)
  const [brand, setBrand] = useState('unknown')
  const formCompleted = complete.card && complete.date && complete.cvc
  const [activeCard, setActiveCard] = useState(defaultCard || 'new')
  const newCard = activeCard == 'new'
  const cardsRef = useRef([])
  const amount = parseFloat(type == 'team' ? league.team_price : league.individual_price)
  const formOptions = {
    style: {
      base: {
        fontWeight: 'lighter',
        fontSize: '16px',
      },
    },
  }

  const createIntent = async () => {
    const params = {
      token: token,
      location_id: league.location_id,
      currency: league.currency,
      amount: amount.toFixed(2),
      app_fee: 0
    }
    const response = await ajaxPost('/plugin/payment/create_intent', params)
    const json = await response.json()
    return json.intent
  }

  const createRegistration = async (options) => {
    formData.append('payment[type]', options.type)
    formData.append('payment[token]', options.token)
    const response = await postForm(`/plugin/leagues/${league.id}/registrations`, formData)
    const json = await response.json()
    setProcessing(false)
    if (json.error) setError(json.error)
    else onComplete(json.data)
  }

  useEffect(() => {
    if (stripe) {
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Plei order',
          amount: Math.round(amount*100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })
      pr.canMakePayment().then(result => {
        if (result) setPaymentRequest(pr)
      })

      pr.on('paymentmethod', async (ev) => {
        const intent = await createIntent()
        if (intent.error) {
          setError(intent.error)
          setProcessing(false)
          return
        }

        const {paymentIntent, error: confirmError} = await stripe.confirmCardPayment(
          intent.secret,
          {payment_method: ev.paymentMethod.id},
          {handleActions: false}
        )
        if (confirmError) {
          ev.complete('fail')
          setError(confirmError.message)
          setProcessing(false)
        }
        else {
          ev.complete('success')
          const paymentOptions = { type: 'card_number', token: paymentIntent.id }
          if (paymentIntent.status === 'requires_action') {
            const {error} = await stripe.confirmCardPayment(intent.secret)
            if (error) {
              setError(error.message)
              setProcessing(false)
            }
            else createRegistration(paymentOptions)
          }
          else createRegistration(paymentOptions)
        }
      })
    }
  }, [stripe])

  useEffect(() => {
    const defaultCardIndex = cards.findIndex(card => card.token == defaultCard)
    if (defaultCardIndex != -1) cardsRef.current[defaultCardIndex].focus()
  }, [cardsRef.current])
  
  const handleSubmit = async () => {
    setProcessing(true)
    setError(null)

    if (!stripe || !elements) return

    const intent = await createIntent()
    if (intent.error) {
      setError(intent.error)
      setProcessing(false)
      return
    }

    if (intent.secret == 'pi_test_valid') {
      return createRegistration({type: 'card_number', token: intent.secret})
    }

    const result = await stripe.confirmCardPayment(intent.secret, {
      payment_method: { card: elements.getElement(CardNumberElement) }
    })

    if (result.error) {
      setError(result.error.message)
      setProcessing(false)
    } else {
      if (result.paymentIntent.status === 'requires_capture') {
        createRegistration({type: 'card_number', token: result.paymentIntent.id})
      }
    }
  }

  const chargeStored = (method) => {
    setProcessing(true)
    createRegistration({type: 'saved_card', token: method})
  }

  const debouncedSubmit = debounce(handleSubmit, 300)

  const onElementChange = (event, name) => {
    if (name == 'card') setBrand(event.brand)
    setComplete({...complete, [name]: event.complete})
  }

  const methodClass = (type) => {
    return 'method-button' + (method == type ? ' active' : '')
  }

  const activeClass = (token) => {
    return activeCard == token ? ' active' : ''
  }

  const checkCardKeyDown = (e, card) => {
    if (e.key === 'Enter') chargeStored(card.token)
  }

  const cardsList = () => {
    return <div className='stored-cards-list'>
      { cards.map((card, i) => {
          return <div 
            className={'stored-card' + activeClass(card.token)}
            key={i}
            ref={el => cardsRef.current[i] = el}
            tabIndex={i}
            onKeyDown={(e) => checkCardKeyDown(e, card)}
            onClick={() => setActiveCard(card.token)}
          >
            <div className='stored-card__desc'>
              <div className={'card-selection' + activeClass(card.token)}></div>
              { card.image_url ? <img src={card.image_url} /> : <div className={'brand-img' + ` ${card.name}-img`}></div> }
              <div>
                <div className='card-ending'>{`${card.title} ending in ${card.last_4}`}</div>
                <div className='card-expiry'>{`Expiry ${card.exp_month}/${card.exp_year}`}</div>
              </div>
            </div>
          </div>
        })
      }
      <div className='stored-card' onClick={() => setActiveCard('new')}>
        <div className='stored-card__desc'>
          <div className={'card-selection' + activeClass('new')}></div>
          <div className='card-new'>New card</div>
        </div>
      </div>
    </div>
  }

  const clickConfirm = (e) => {
    if (!newCard) {
      e.preventDefault()
      chargeStored(activeCard)
    }
  }

  const submitDisabled = newCard && (!stripe || !formCompleted || processing)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const isCard = method == 'card'
  const isPay = method == 'pay'

  return (
    <form className={`payment-form ${processing ? ' isLoading' : ''}`} onSubmit={e => {e.preventDefault(); debouncedSubmit()}}>
      { error && <div className='payment-error'>{error}</div> }
      <div className='payment-method'>Payment Method</div>
      <div className='payment-methods'>
        <button className={methodClass('card')} disabled={isCard} onClick={() => setMethod('card')}>
          <img src='/images/leagues/credit-card.svg'/>
        </button>
        <button className={methodClass('pay')} disabled={isPay} onClick={() => setMethod('pay')}>
          { isSafari ? <img src='/images/leagues/apple-pay.svg' /> : <img src='/images/leagues/google-pay.svg' /> }
        </button>
      </div>
      { isPay &&
        <div className='pay-block'>
         { paymentRequest && <PaymentRequestButtonElement options={{paymentRequest}} />}
          <div className='payment-buttons'><div className='button button-back' onClick={onBack}>Back</div></div>
        </div>
      }
      { isCard &&
        <div>
          { processing && <div className='animated-spinner'></div> }
          { cardsList() }
          { newCard && 
            <div className='payment-form-card-number'>
              <label className='payment-label' htmlFor='cardnumber'>
                Card Number
                <div className='payment-card-input'>
                  <CardNumberElement options={{...formOptions, placeholder: 'Card Number'}} onChange={(event) => onElementChange(event, 'card')} />
                  <div className={'brand-img' + ` ${brand}-img`}></div>
                </div>
              </label>
              <div className='payment-form-expiration-cvc'>
                <label className='payment-label' htmlFor='exp-date'>
                  EXP
                  <CardExpiryElement options={formOptions} onChange={(event) => onElementChange(event, 'date')} />
                </label>
                <label className='payment-label' htmlFor='cvc'>
                  CVV
                  <CardCvcElement options={formOptions} onChange={(event) => onElementChange(event, 'cvc')} />
                </label>
              </div>
            </div>
          }
          <div className='payment-buttons'>
            <div className='button button-back' onClick={onBack}>Back</div>
            <button className='button button-next' disabled={submitDisabled} onClick={clickConfirm}>
              Confirm and Pay
            </button>
          </div>
        </div>
      }
    </form>
  )
}
